import { CartAddEvent } from '@theme/events';
import { pairTotal, productQuantity, secondUnitOffer } from '@theme/crown-second-unit-math';

const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency }).format(amount / 100);
const cartURL = () => `${Theme.routes.cart_url}.js`;
const eligibleVariants = (element) => element.dataset.eligibleVariants?.trim().split(',') || [];
const active = (element) => !element.hasAttribute('data-crown-energy') || document.documentElement.hasAttribute('data-crown-energy-active');

class CrownPairPicker extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.form = this.closest('product-form-component');
    this.section = this.closest('.shopify-section');
    this.classList.add('is-ready');
    document.addEventListener('crown:energy-schedule', () => this.syncAvailability(), options);
    this.addEventListener('change', () => this.syncQuantity(), options);
    this.section?.addEventListener('variant:update', (event) => this.updateVariant(event), options);
    document.addEventListener('cart:update', (event) => {
      if (event.detail.resource?.items) this.updateCart(event.detail.resource);
    }, options);
    this.syncAvailability();
  }

  disconnectedCallback() { this.abort?.abort(); }

  syncAvailability() {
    this.hidden = this.dataset.unavailable === 'true' || !active(this);
    this.form?.classList.toggle('crown-pair-unavailable', this.hidden);
    if (!this.hidden) this.syncQuantity();
  }

  syncQuantity() {
    const quantity = this.querySelector('input:checked')?.value || '1';
    const native = this.form?.querySelector('quantity-selector-component');
    const input = native?.querySelector('input[name="quantity"]');
    if (input) input.value = quantity;
    native?.setValue?.(quantity);
    this.renderShipping(Number(quantity));
  }

  renderShipping(quantity = Number(this.querySelector('input:checked')?.value || 1)) {
    const price = Number(this.dataset.price);
    const previous = Number(this.dataset.cartQuantity);
    const saving = Number(this.dataset.saving);
    const projected = Number(this.dataset.cartTotal) + pairTotal(price, previous + quantity, saving) - pairTotal(price, previous, saving);
    const threshold = Number(this.dataset.threshold);
    const remaining = Math.max(0, threshold - projected);
    const text = remaining ? this.dataset.remaining.replace('__amount__', money(remaining, this.dataset.currency)) : this.dataset.reached;
    this.querySelector('[data-shipping-message]').textContent = text;
    this.querySelector('progress').value = Math.min(projected, threshold);
  }

  updateCart(cart) {
    this.dataset.cartTotal = cart.total_price;
    this.dataset.cartQuantity = productQuantity(cart, this.dataset.productId, eligibleVariants(this));
    if (!this.hidden) this.renderShipping();
  }

  updateVariant(event) {
    if (String(event.detail.data.productId) !== this.dataset.productId) return;
    const next = event.detail.data.html.querySelector(`crown-pair-picker[data-product-id="${this.dataset.productId}"]`);
    const price = this.section.querySelector('[data-crown-second-price]');
    const nextPrice = event.detail.data.html.querySelector('[data-crown-second-price]');
    if (price && nextPrice) price.innerHTML = nextPrice.innerHTML;
    if (!next) { this.dataset.unavailable = 'true'; this.syncAvailability(); return; }
    const selected = this.querySelector('input:checked')?.value || '1';
    const total = this.dataset.cartTotal;
    for (const key of Object.keys(this.dataset)) delete this.dataset[key];
    Object.assign(this.dataset, next.dataset);
    if (total !== undefined) this.dataset.cartTotal = total;
    this.innerHTML = next.innerHTML;
    const input = this.querySelector(`input[value="${selected}"]`);
    if (input) input.checked = true;
    this.syncAvailability();
  }

  offer(cart, variantId, quantity) {
    if (this.hidden || !active(this)) return false;
    const item = secondUnitOffer(cart, this.dataset.productId, variantId, quantity, Number(this.dataset.max), eligibleVariants(this));
    const dialog = this.closest('.buy-buttons-block')?.querySelector('crown-second-dialog');
    return item && dialog ? dialog.openOffer(item) : false;
  }
}

class CrownSecondDialog extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.dialog = this.querySelector('dialog');
    document.addEventListener('crown:energy-schedule', () => { if (!active(this) && this.dialog.open) this.dialog.close(); }, options);
    this.querySelectorAll('[data-decline]').forEach((button) => button.addEventListener('click', () => this.dialog.close(), options));
    this.querySelector('[data-accept]').addEventListener('click', () => this.accept(), options);
    this.dialog.addEventListener('close', () => document.querySelector('cart-drawer-component')?.open(), options);
    this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog && event.offsetX < 0) this.dialog.close(); }, options);
  }

  disconnectedCallback() { this.abort?.abort(); }

  openOffer(item) {
    if (this.dialog.open || !active(this)) return false;
    this.item = item;
    this.querySelector('[data-offer-original]').textContent = money(Number(this.dataset.reference) || item.original_price, this.dataset.currency);
    this.querySelector('[data-offer-price]').textContent = money(pairTotal(item.original_price, 2, this.dataset.saving ? Number(this.dataset.saving) : undefined) - item.original_price, this.dataset.currency);
    this.querySelector('[data-offer-variant]').textContent = item.variant_title || '';
    const image = this.querySelector('img');
    if (item.featured_image?.url) {
      const imageURL = new URL(item.featured_image.url, window.location.origin);
      imageURL.searchParams.set('width', '200');
      image.removeAttribute('srcset');
      image.src = imageURL.href;
    }
    this.querySelector('[data-offer-error]').hidden = true;
    this.dialog.showModal();
    return true;
  }

  async accept() {
    const button = this.querySelector('[data-accept]');
    if (button.disabled) return;
    button.disabled = true;
    this.querySelector('[data-offer-error]').hidden = true;
    try {
      const beforeResponse = await fetch(cartURL());
      if (!beforeResponse.ok) throw new Error(this.dataset.error);
      const before = await beforeResponse.json();
      if (!active(this) || !secondUnitOffer(before, this.dataset.productId, this.item.variant_id, 1, 0, eligibleVariants(this))) {
        this.dialog.close();
        document.dispatchEvent(new CartAddEvent(before, 'crown-second-unit', { source: 'crown-second-unit', itemCount: before.item_count }));
        return;
      }
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId))];
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: this.item.variant_id, quantity: 1 }], sections, sections_url: window.location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
      this.dialog.close();
      const cartResponse = await fetch(cartURL()).catch(() => null);
      const cart = cartResponse?.ok ? await cartResponse.json() : undefined;
      document.dispatchEvent(new CartAddEvent(cart, 'crown-second-unit', { source: 'crown-second-unit', sections: result.sections, itemCount: cart?.item_count ?? before.item_count + 1 }));
    } catch (error) {
      const message = this.querySelector('[data-offer-error]');
      message.textContent = error.message || this.dataset.error;
      message.hidden = false;
    } finally { button.disabled = false; }
  }
}

customElements.define('crown-pair-picker', CrownPairPicker);
customElements.define('crown-second-dialog', CrownSecondDialog);
